import { FC, useState, useCallback } from 'react';

import {
    ButtonSection,
    Container,
    MergePrompt,
    MergePromptTitle,
    MergePromptSubtitle
} from './styled';

import useAppDispatch from 'src/hooks/useAppDispatch';
import QuestActionButton from '../ReviewButton';
import { postRequestApprove } from 'src/redux/quests/service';
import { setSnackbarAttributes } from 'src/redux/snackbar/slice';

interface ApproveProps {
    questId: string;
    onSuccess: () => void;
    onCancel: () => void;
}

const Approve: FC<ApproveProps> = ({ questId, onSuccess, onCancel }) => {
    const [submittingChoice, setSubmittingChoice] = useState<
        null | 'merge' | 'no_merge'
    >(null);
    const dispatch = useAppDispatch();

    const submitApprovalRequest = useCallback(
        async (merge: boolean) => {
            try {
                await dispatch(
                    postRequestApprove({
                        questId,
                        params: { feedback: '', merge }
                    })
                ).unwrap();
                dispatch(
                    setSnackbarAttributes({
                        snackbarOpen: true,
                        snackbarType: 'success',
                        snackbarMessage: merge
                            ? "Request approved. We'll handle your merge request shortly."
                            : 'Request approved successfully.'
                    })
                );
                onSuccess();
            } catch (error) {
                console.error('Error approving quest:', error);
                dispatch(
                    setSnackbarAttributes({
                        snackbarOpen: true,
                        snackbarType: 'error',
                        snackbarMessage:
                            'An error occurred while approving the request. Please try again or contact support.'
                    })
                );
            }
        },
        [dispatch, onSuccess, questId]
    );

    const handleApproveWithMerge = useCallback(async () => {
        setSubmittingChoice('merge');
        await submitApprovalRequest(true);
        setSubmittingChoice(null);
    }, [submitApprovalRequest]);

    const handleApproveWithoutMerge = useCallback(async () => {
        setSubmittingChoice('no_merge');
        await submitApprovalRequest(false);
        setSubmittingChoice(null);
    }, [submitApprovalRequest]);

    return (
        <Container>
            <MergePrompt>
                <MergePromptTitle>
                    Do you want to merge this request?
                </MergePromptTitle>
                <MergePromptSubtitle>
                    Selecting yes will trigger the automatic merge of the
                    request's pull requests right after approval.
                </MergePromptSubtitle>
            </MergePrompt>
            <ButtonSection>
                <QuestActionButton
                    label="Cancel"
                    onClick={onCancel}
                    width="100px"
                    styleType="codGray"
                    disabled={submittingChoice !== null}
                />
                <QuestActionButton
                    label="No"
                    onClick={handleApproveWithoutMerge}
                    width="100px"
                    styleType="codGray"
                    isLoading={submittingChoice === 'no_merge'}
                    disabled={submittingChoice !== null}
                />
                <QuestActionButton
                    label="Yes"
                    onClick={handleApproveWithMerge}
                    width="100px"
                    styleType="yellow"
                    isLoading={submittingChoice === 'merge'}
                    disabled={submittingChoice !== null}
                />
            </ButtonSection>
        </Container>
    );
};

export default Approve;
